/**
 * Google Calendar API Service
 * Handles all Google Calendar integration-related API calls to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const fetchWithAuth = async (url, options = {}) => {
    const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        credentials: 'include',
    });

    if (response.status === 401) {
        console.error('Authentication expired or failed for:', url);
        localStorage.removeItem('currentUser');
        window.location.href = '/login';
        throw new Error('Unauthorized');
    }

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.detail || errorData.message || 'API request failed');
    }

    // Handle redirect responses (for OAuth callback)
    if (response.redirected || response.status === 302) {
        return { redirected: true, url: response.url };
    }

    return response.json();
};

/**
 * Initiate Google Calendar OAuth authorization
 */
export const authorizeGoogleCalendar = async (requestData) => {
    return fetchWithAuth('/v1/google-calendar/authorize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
    });
};

/**
 * Get list of connected Google Calendar integrations
 */
export const getGoogleCalendars = async (familyId = null) => {
    const url = familyId 
        ? `/v1/google-calendar/calendars?family_id=${familyId}`
        : '/v1/google-calendar/calendars';
    return fetchWithAuth(url);
};

/**
 * Get a specific Google Calendar integration
 */
export const getGoogleCalendar = async (integrationId) => {
    return fetchWithAuth(`/v1/google-calendar/calendars/${integrationId}`);
};

/**
 * Update a Google Calendar integration
 */
export const updateGoogleCalendar = async (integrationId, updateData) => {
    return fetchWithAuth(`/v1/google-calendar/calendars/${integrationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
    });
};

/**
 * Remove a Google Calendar integration
 */
export const removeGoogleCalendar = async (integrationId) => {
    return fetchWithAuth(`/v1/google-calendar/calendars/${integrationId}`, {
        method: 'DELETE',
    });
};

/**
 * Manually sync events from a Google Calendar
 */
export const syncGoogleCalendar = async (integrationId) => {
    return fetchWithAuth(`/v1/google-calendar/calendars/${integrationId}/sync`, {
        method: 'POST',
    });
};
